import { employeeService, restaurantOrderService } from '@kss/api';

/**
 * Picks the next waiter (round-robin) and stamps the order with
 * `status = "WaiterAssigned"` plus the chosen waiter's id.
 *
 * Why client-side?
 *   The live API doesn't ship a server-side dispatcher yet (§30).
 *   Doing it here means the floor manager doesn't have to babysit a
 *   queue — the customer's "Place Order" tap puts the ticket on a
 *   real waiter's WaiterQueue page within ~1 second.
 *
 * Selection rule:
 *   1. Pull `Employee.all()`, filter to active rows whose
 *      `designation`, `department`, or `name` token looks waiter-ish.
 *   2. Fallback to ALL active employees if no match (so a single-
 *      person setup still works).
 *   3. Round-robin via a `localStorage` counter scoped to the tenant
 *      so two customers placing at the same time don't both pick the
 *      same waiter.
 *
 * Returns the chosen waiter's id (or null if no employees at all).
 * Best-effort — silently skips errors so a flaky network can't block
 * the customer's order from being recorded.
 */
export async function autoAssignWaiter(orderId: string | number): Promise<string | null> {
  let chosenId: string | null = null;
  try {
    const employees = await employeeService.all();
    if (!Array.isArray(employees) || employees.length === 0) return null;

    const active = employees.filter((e) => {
      const v = e.isActive as unknown as boolean | number | undefined;
      return v === undefined || (v !== false && v !== 0);
    });

    const isWaiter = (e: typeof active[number]): boolean => {
      const blob = [
        (e as { designation?: string }).designation,
        (e as { department?: string }).department,
        (e as { role?: string }).role,
        e.firstName ?? '',
        e.lastName ?? '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return /waiter|server|steward|captain|host/.test(blob);
    };

    const pool = active.filter(isWaiter);
    const candidates = pool.length > 0 ? pool : active;
    if (candidates.length === 0) return null;

    const counterKey = 'kss.dine.waiterRoundRobin';
    const prev = Number(localStorage.getItem(counterKey) || '0');
    const idx = prev % candidates.length;
    const chosen = candidates[idx];
    localStorage.setItem(counterKey, String(prev + 1));

    chosenId = chosen.employeeId != null
      ? String(chosen.employeeId)
      : (chosen as { id?: string | number }).id != null
        ? String((chosen as { id?: string | number }).id)
        : null;

    if (!chosenId) return null;

    // Stamp the order with the new status + assigned waiter.
    // Patch through the bulk CRUD update — same shape Kitchen Display
    // uses to advance status (the row spec is { id, name, status, ... }).
    await restaurantOrderService
      .update([
        {
          id: Number(orderId),
          // workflow stage → string `orderStatus`; numeric `status` stays 0
          status: 0,
          orderStatus: 'WaiterAssigned',
          // remarks carries the assignment audit trail until §29.2 ships
          // dedicated `assignedToUserId` + `assignedAt` columns.
          remarks: `WaiterAssigned@${new Date().toISOString().slice(11, 16)}|waiter=${chosenId}`,
          isActive: 1,
        } as unknown as Parameters<typeof restaurantOrderService.update>[0][number],
      ])
      .catch(() => undefined);

    return chosenId;
  } catch {
    return chosenId;
  }
}
